import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Volume2, Circle, Square, ChevronRight, SkipForward, Check, Loader2, Video, Mic, Sparkles, Pause } from "lucide-react";
import api from "@/lib/api";
import { chunkedUpload, pickMime } from "@/lib/upload";

export default function StoryInterview() {
  const { storyId, chapterKey } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [questions, setQuestions] = useState([]);   // array of {text, followup}
  const [idx, setIdx] = useState(0);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [audioOnly, setAudioOnly] = useState(false);
  const [phase, setPhase] = useState("idle");        // idle | recording | uploading | answered
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [followup, setFollowup] = useState("");
  const [reducedMotion, setReducedMotion] = useState(false);

  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("");
  const timerRef = useRef(null);
  const ttsAudioRef = useRef(null);

  useEffect(() => {
    setReducedMotion(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false);
  }, []);

  const load = useCallback(async () => {
    try {
      const [st, q] = await Promise.all([
        api.get(`/stories/${storyId}`),
        api.get(`/stories/${storyId}/chapters/${chapterKey}/questions`),
      ]);
      setStory(st.data);
      setChapter(q.data.chapter);
      setQuestions((q.data.questions || []).map((t) => ({ text: t, followup: false })));
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Not found"); navigate("/dashboard"); }
    }
  }, [storyId, chapterKey, navigate]);

  useEffect(() => { load(); return () => cleanup(); /* eslint-disable-next-line */ }, [load]);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (ttsAudioRef.current) { ttsAudioRef.current.pause(); ttsAudioRef.current = null; }
  };

  const current = questions[idx];

  const playQuestion = async () => {
    if (!current) return;
    try {
      const res = await api.post(`/stories/${storyId}/tts`, { text: current.text }, { responseType: "blob" });
      if (res.status === 204 || !res.data || res.data.size === 0) { setTtsEnabled(false); toast.message("Spoken questions aren't set up yet — please read the question aloud."); return; }
      const url = URL.createObjectURL(res.data);
      if (ttsAudioRef.current) ttsAudioRef.current.pause();
      const a = new Audio(url); ttsAudioRef.current = a; a.play();
    } catch { setTtsEnabled(false); }
  };

  const startRecording = async () => {
    try {
      const constraints = audioOnly ? { audio: true } : { audio: true, video: { facingMode: "user", width: { ideal: 1280 } } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (!audioOnly && liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.play().catch(() => {}); }
      const mime = pickMime(audioOnly);
      mimeRef.current = mime;
      chunksRef.current = [];
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = onStop;
      recRef.current = rec;
      rec.start(1000);
      setElapsed(0); setPhase("recording"); setTranscript(""); setFollowup("");
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch {
      toast.error("We couldn't reach your camera or microphone. Please allow access and try again.");
    }
  };

  const stopRecording = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const onStop = async () => {
    setPhase("uploading");
    try {
      const blob = new Blob(chunksRef.current, { type: mimeRef.current || (audioOnly ? "audio/webm" : "video/webm") });
      const ext = (mimeRef.current || "").includes("mp4") ? (audioOnly ? "m4a" : "mp4") : "webm";
      const up = await chunkedUpload(blob, `answer.${ext}`, mimeRef.current || "video/webm");
      const res = await api.post(`/stories/${storyId}/chapters/${chapterKey}/answers`, {
        file_id: up.file_id, question: current?.text || "", kind: audioOnly ? "audio" : "video",
        duration: elapsed, is_followup: !!current?.followup,
      });
      setTranscript(res.data.transcript || "");
      setFollowup(res.data.suggested_followup || "");
      setTtsEnabled(res.data.tts_enabled !== false ? ttsEnabled : false);
      setPhase("answered");
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      toast.success("Saved — beautifully done.");
    } catch (e) {
      setPhase("idle");
      toast.error("We couldn't save that answer. Please try again.");
    }
  };

  const acceptFollowup = () => {
    const q = { text: followup, followup: true };
    const next = [...questions]; next.splice(idx + 1, 0, q);
    setQuestions(next); setFollowup(""); goNext(next);
  };

  const goNext = (list = questions) => {
    setTranscript(""); setFollowup(""); setPhase("idle");
    if (idx + 1 < list.length) setIdx(idx + 1);
    else finishChapter();
  };

  const finishChapter = async () => {
    try { await api.post(`/stories/${storyId}/chapters/${chapterKey}/complete`); } catch {}
    toast.success("Chapter saved. Thank you for these memories.");
    navigate(`/stories/${storyId}`);
  };

  const saveExit = () => { cleanup(); navigate(`/stories/${storyId}`); };

  const mmss = useMemo(() => `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`, [elapsed]);
  const progressPct = questions.length ? Math.round(((idx) / questions.length) * 100) : 0;

  if (!story || !chapter) return <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]"><Loader2 className="animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
      {/* top bar */}
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <button data-testid="interview-save-exit-button" onClick={saveExit} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:underline"><ArrowLeft size={16} /> Save & come back later</button>
          <div className="font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{chapter.code} · {chapter.label}</div>
        </div>
        <div className="h-1 bg-[hsl(var(--muted))]"><div className="h-full bg-[hsl(var(--accent))] transition-[width] duration-500" style={{ width: `${Math.max(3, progressPct)}%` }} data-testid="interview-chapter-progress" /></div>
      </div>

      {/* letterbox studio */}
      <div className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <p className="font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Question {idx + 1}{current?.followup ? " · follow-up" : ""}</p>
          <h1 data-testid="interview-question-text" className="mt-2 font-display text-2xl sm:text-3xl md:text-4xl leading-tight max-w-3xl">{current?.text}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button data-testid="interview-play-question-button" onClick={playQuestion} disabled={phase === "recording"}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-base font-medium hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50 min-h-[44px]">
              <Volume2 size={18} /> Hear the question
            </button>
            <button onClick={() => setAudioOnly((v) => !v)} disabled={phase === "recording" || phase === "uploading"}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] text-base hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50 min-h-[44px]">
              {audioOnly ? <Mic size={18} /> : <Video size={18} />} {audioOnly ? "Audio only" : "Video"}
            </button>
          </div>
          <p className="mt-3 text-base sm:text-lg text-[hsl(var(--muted-foreground))]">Take your time — you can pause and come back anytime.</p>

          {/* recorder stage */}
          <div className="mt-6 rounded-2xl overflow-hidden border border-[hsl(var(--film-border))] bg-[hsl(var(--letterbox))]">
            <div className="aspect-video relative grid place-items-center">
              {!audioOnly && <video ref={liveRef} muted playsInline className="absolute inset-0 h-full w-full object-cover" />}
              {phase === "idle" && (
                <div className="relative z-10 text-center text-[hsl(var(--letterbox-foreground))]">
                  <p className="text-lg opacity-90">When you're ready, press record.</p>
                </div>
              )}
              {phase === "recording" && (
                <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 text-white">
                  <span className={`h-2.5 w-2.5 rounded-full bg-[hsl(var(--destructive))] ${reducedMotion ? "" : "ever-rec-pulse"}`} />
                  <span className="font-mono text-sm tabular-nums">{mmss}</span>
                </div>
              )}
              {phase === "uploading" && (
                <div className="relative z-10 text-center text-[hsl(var(--letterbox-foreground))]"><Loader2 className="mx-auto animate-spin" /><p className="mt-2">Saving your answer…</p></div>
              )}
              {phase === "answered" && (
                <div className="relative z-10 text-center text-[hsl(var(--letterbox-foreground))]"><Check className="mx-auto" size={28} /><p className="mt-2 text-lg">Saved</p></div>
              )}
            </div>
          </div>

          {/* transcript + follow-up */}
          {phase === "answered" && transcript && (
            <div className="mt-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
              <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">What we heard</div>
              <p className="mt-1.5 text-sm leading-7 text-[hsl(var(--foreground))]">{transcript}</p>
            </div>
          )}
          {phase === "answered" && followup && (
            <div className="mt-4 rounded-xl border-2 border-[hsl(var(--accent))] bg-[hsl(var(--theme-accent-soft))] p-4" data-testid="interview-followup-callout">
              <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(var(--theme-accent-ink))]"><Sparkles size={15} /> A gentle follow-up</div>
              <p className="mt-1.5 font-display text-lg leading-snug">{followup}</p>
              <div className="mt-3 flex gap-2">
                <button data-testid="interview-followup-accept" onClick={acceptFollowup} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold min-h-[44px]"><Check size={16} /> Ask this next</button>
                <button data-testid="interview-followup-skip" onClick={() => setFollowup("")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] min-h-[44px]">Skip</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* sticky controls */}
      <div className="sticky bottom-0 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          {phase === "idle" && (
            <button data-testid="interview-record-toggle-button" onClick={startRecording}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--destructive))] text-white font-semibold text-base min-h-[52px]"><Circle size={18} fill="currentColor" /> Start recording</button>
          )}
          {phase === "recording" && (
            <button data-testid="interview-record-toggle-button" onClick={stopRecording}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--foreground))] text-[hsl(var(--background))] font-semibold text-base min-h-[52px]"><Square size={16} fill="currentColor" /> Stop</button>
          )}
          {phase === "uploading" && (
            <button disabled className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] font-semibold text-base min-h-[52px]"><Loader2 size={18} className="animate-spin" /> Saving…</button>
          )}
          {phase === "answered" && !followup && (
            <button data-testid="interview-next-button" onClick={() => goNext()} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-base min-h-[52px]">
              {idx + 1 < questions.length ? <>Next question <ChevronRight size={18} /></> : <>Finish chapter <Check size={18} /></>}
            </button>
          )}
          <div className="flex-1" />
          {(phase === "idle" || phase === "answered") && (
            <button data-testid="interview-skip-button" onClick={() => goNext()} className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border border-[hsl(var(--border))] font-medium hover:bg-[hsl(var(--muted))] transition-colors min-h-[44px]"><SkipForward size={16} /> Skip</button>
          )}
          <button data-testid="interview-pause-button" onClick={saveExit} className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border border-[hsl(var(--border))] font-medium hover:bg-[hsl(var(--muted))] transition-colors min-h-[44px]"><Pause size={16} /> Pause</button>
        </div>
      </div>
    </div>
  );
}
